import { PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Input,
  message,
  Row,
  Select,
  Space,
  Spin,
  Tag,
} from "antd";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { getSupaClient, getWorkspace } from "../../lib/supabase";
import { setTeamTagsData } from "../../redux/actions";
import { TAG_COLORS } from "../../utils/constants";
import { DateRangePicker } from "../common";
import ChannelMemberFilter from "./ChannelMemberFilter";
import GroupFilter from "./GroupFilter";
import RoleFilter from "./RoleFilter";
import TimezoneFilter from "./TimezoneFilter";

const { Option } = Select;
const { TextArea } = Input;
const defaultTimeFormat = "YYYY-MM-DDTHH:mm:ssZ";
const dateFormat = "MM/DD/YY";
const { RangePicker } = DatePicker;

const TagFilter = (props) => {
  /** *
   *  props.memberId REQUIRED.
   *  Optional: memberTags along with OnTeamTagAdded and teamTags if you already have a list
   *  If only memberTags isnt provided this component will assume you want to pull the member tags and team tags in the compoennt itself
   */

  // props.onChange --> function in parent component to call when tag is selected/deselected
  // props.teamId --> teamId used to fetch tags for team
  const dispatch = useDispatch();
  const supabase = getSupaClient();
  const workspace = getWorkspace(supabase);
  const platform = localStorage.getItem("platform");
  const [memberTags, setMemberTags] = useState([]);
  const [newTag, setNewTag] = useState("");
  const [errors, setErrors] = useState(null);
  const [memberTagsId, setMemberTagsId] = useState([]);
  // const teamTags = useSelector((state) => state.teamTags.team_tags);
  const [teamTags, setTeamTags] = useState([]);

  const loadTeamTags = async () => {
    let query = supabase.from("tags").select("id, name, color");
    if (platform === "slack") query = query.eq("team", props.teamId);
    else query = query.eq("guild", props.teamId);
    const { data, error } = await query;
    if (data) {
      setTeamTags(data);
    }
    if (error) {
      console.log("Error", error);
    }
  };

  useEffect(() => {
    loadTeamTags();
  }, []);

  const onTagNameChange = (event) => {
    event.preventDefault();
    setNewTag(event.target.value);
  };

  function returnTagObj(value) {
    return teamTags.find((item) => item.id === value);
  }

  function tagRender(payload) {
    console.log("Executing tagRender!");
    const { onClose, closable, value } = payload;
    if (payload.value) {
      const memberTag = memberTags.find(
        (element) => element.id === payload.value
      );
      console.log("tagRender - memberTag:", memberTag);
      if (memberTag) {
        return (
          <Tag
            color={memberTag.color}
            closable={closable}
            onClose={onClose}
            style={{ marginRight: 3 }}
            key={value}
          >
            {memberTag.name}
          </Tag>
        );
      }
      return null;
    }
    return null;
  }

  const onSelect = async (selection) => {
    const selectedTag = returnTagObj(selection);
    const newMemberTags = memberTags.concat(selectedTag);
    const newMemberTagsIds = newMemberTags.map((item) => item.id);
    setMemberTags(newMemberTags);
    setMemberTagsId(newMemberTagsIds);
    props.onChange({
      filter_type: "tags",
      filter_settings: { tags: newMemberTagsIds },
    });
  };

  const onDeselect = async (selection) => {
    const remainingTags = memberTags.filter((tag) => tag.id !== selection);
    const remainingTagsIds = remainingTags.map((item) => item.id);
    setMemberTags(remainingTags);
    setMemberTagsId(remainingTags.map((item) => item.id));
    if (remainingTagsIds.length === 0) {
      props.onChange({ filter_type: "tags" });
    } else {
      props.onChange({
        filter_type: "tags",
        filter_settings: { tags: remainingTagsIds },
      });
    }
  };

  const getNewTagColor = () => {
    let tagColor = "";
    if (teamTags.length < TAG_COLORS.length) {
      tagColor = TAG_COLORS[teamTags.length];
    } else {
      tagColor = TAG_COLORS[teamTags.length % TAG_COLORS.length];
    }
    return tagColor;
  };

  const addItem = async () => {
    const tagColor = getNewTagColor();
    const { data, error } = await supabase
      .from("tags")
      .insert([
        { name: newTag, team: props.teamId, color: tagColor, workspace },
      ]);

    if (data) {
      window.analytics.track("New team tag added");

      dispatch(
        setTeamTagsData(
          teamTags.concat({
            id: data[0].id,
            name: data[0].name,
            color: data[0].color,
          })
        )
      );

      setMemberTags(memberTags.concat(data[0]));
      setMemberTagsId(memberTagsId.concat(data[0].id));
      setNewTag("");
      setErrors();
    }
    if (error) {
      setErrors({ teamTag: true });
    }
  };

  return (
    <>
      <Space>
        <span>Select tags: </span>
        <Select
          mode="multiple"
          showArrow
          tagRender={tagRender}
          value={memberTagsId}
          style={{ width: "500px", borderRadius: 10 }}
          onSelect={onSelect}
          onDeselect={onDeselect}
          optionFilterProp="name"
          dropdownRender={(menu) => (
            <div>
              {menu}
              <Divider style={{ margin: "4px 0" }} />
              <div
                style={{
                  display: "flex",
                  flexWrap: "nowrap",
                  padding: 8,
                }}
              >
                <Input
                  style={{ flex: "auto", borderRadius: 5 }}
                  value={newTag}
                  onChange={onTagNameChange}
                  onKeyDown={(e) => e.stopPropagation()}
                />
                <div className="pl-2">
                  <Button
                    type="primary"
                    style={{
                      flex: "none",
                      display: "block",
                      cursor: "pointer",
                    }}
                    onClick={addItem}
                    disabled={newTag === ""}
                  >
                    + New tag
                  </Button>
                </div>
              </div>
              {errors && errors.teamTag ? (
                <p>Error adding tag, try again please</p>
              ) : (
                ""
              )}
            </div>
          )}
        >
          {teamTags.map((item) => {
            return (
              <Option key={item.id} value={item.id} name={item.name}>
                <Tag
                  style={{
                    border: `0.5px solid ${item.color}`,
                    borderRadius: 5,
                    borderWidth: "thin",
                    width: "fit-content",
                  }}
                >
                  {item.name}
                </Tag>
              </Option>
            );
          })}
        </Select>
      </Space>
    </>
  );
};

const CreateNewView = (props) => {
  // props.teamId - slack teams slack_id or discord_guilds discord_id value
  // props.onChange -- callback in parent component when new view is created

  const [startTime, setStartTime] = useState(
    moment().subtract(7, "d").format(dateFormat)
  );
  const [endTime, setEndTime] = useState(moment().format(dateFormat));
  window.moment = moment;
  const disabledDate = (current) => {
    // Can not select days before today and today
    return current && current > moment().endOf("day");
  };
  const supabase = getSupaClient();
  const workspace = getWorkspace(supabase);
  const platform = localStorage.getItem("platform");
  const filterTypes =
    platform === "slack"
      ? ["tags", "date_joined", "channel_member", "user_group", "timezone"]
      : ["tags", "date_joined", "roles"];
  const teamId =
    platform === "slack"
      ? localStorage.getItem("team-id")
      : localStorage.getItem("guild-id");

  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [filters, setFilters] = useState([]); // TODO: handle multiple filters
  const [selectedFilterType, setSelectedFilterType] = useState("");
  const [currentFilter, setCurrentFilter] = useState({});

  const filterOptions = [];
  const [datePickerText, setDatePickerText] = useState(
    props.text ? props.text : "Past 7 days"
  );

  const teamCreatedAt = moment(localStorage.getItem("team-created-at")).format(
    "Do MMMM YYYY"
  );

  useEffect(async () => {
    // Fetching created at of team if not already stored in local storage
    if (localStorage.getItem("team-created-at") === null) {
      const { data, error } = await supabase
        .from("teams")
        .select("created_at")
        .match({ slack_id: teamId });
      localStorage.setItem("team-created-at", data[0].created_at);
    }
  }, []);

  /**
   * Creates a custom user_view in the DB
   */
  const createView = async () => {
    if (name.length === 0) {
      message.error("Name is missing");
      return;
    }
    // const createdFilters = filters; TODO: handle multiple filters
    if (currentFilter.filter_type) {
      if (typeof currentFilter.filter_settings === "undefined") {
        message.error(`Error in ${currentFilter.filter_type} filter`);
      }
      // createdFilters.push(currentFilter); TODO: handle multiple filters
    } else {
      message.error(`Please add a filter`);
      return;
    }

    console.log("view", name, description, currentFilter, teamId);
    setLoading(true);
    const insertView = {
      name,
      description,
      workspace,
    };

    if (platform === "slack") insertView.team = teamId;
    else insertView.guild = teamId;

    // Create the user_view
    const view = await supabase.from("user_views").insert(insertView);
    if (view.error) {
      message.error("Error creating view, please contact admins.");
      setLoading(false);
    } else {
      // TODO: handle multiple filters, changed to iteration
      const insertFilter = {
        view: view.data[0].id,
        ...currentFilter,
        workspace,
      };

      if (platform === "slack") insertFilter.team = teamId;
      else insertFilter.guild = teamId;

      // Create the user_filter for the user_view
      const filter = await supabase.from("user_filters").insert(insertFilter);
      if (filter.error) {
        message.error("Error creating view, please contact admins.");
        setLoading(false);
      } else {
        message.success("Successfully created view!");
        window.analytics.track("New view created", {
          type: currentFilter.filter_type,
        });
        props.onChange();
        setLoading(false);
        setSelectedFilterType("");
        setCurrentFilter({});
        setName("");
        setDescription("");
      }
    }
  };

  const dateChanged = async (date, dateString) => {
    setStartTime(date[0].format(dateFormat));
    setEndTime(date[1].format(dateFormat));
    setDatePickerText(
      `${moment(startTime).format(dateFormat)} - ${moment(endTime).format(
        dateFormat
      )}`
    );
  };

  const onNameChange = (event) => {
    setName(event.target.value);
  };

  const onDescriptionChange = (event) => {
    setDescription(event.target.value);
  };

  const changeSelectedFilterType = (value) => {
    setCurrentFilter(
      value === "date_joined"
        ? {
            filter_type: "date_joined",
            filter_settings: {
              start_date: moment(startTime).format(dateFormat),
              end_date: moment(endTime).format(dateFormat),
            },
          }
        : {}
    );
    setSelectedFilterType(value);
  };

  // eslint-disable-next-line no-restricted-syntax
  for (const filterType of filterTypes) {
    filterOptions.push(<Option value={filterType}>{filterType}</Option>);
  }

  return (
    <div>
      <Spin spinning={loading}>
        <Card title="Create a new view:">
          <Row>
            <Input
              style={{ width: "300px" }}
              value={name}
              onChange={onNameChange}
              placeholder="Name"
            />
          </Row>
          <Row style={{ marginTop: "1em" }}>
            <TextArea
              rows={2}
              style={{ width: "600px" }}
              value={description}
              onChange={onDescriptionChange}
              placeholder="Description"
            />
          </Row>
          <Row style={{ marginTop: "1em" }}>
            <h1>Add filters:</h1>
          </Row>
          <Row style={{ marginTop: "1em" }}>
            <Space>
              <span> Filter type:</span>
              <Select
                value={selectedFilterType}
                style={{ width: "200px" }}
                onChange={changeSelectedFilterType}
              >
                {filterOptions}
              </Select>
            </Space>
          </Row>

          {selectedFilterType === "tags" ? (
            <Row style={{ marginTop: "1em" }}>
              <TagFilter teamId={teamId} onChange={setCurrentFilter} />
            </Row>
          ) : null}

          {selectedFilterType === "roles" ? (
            <Row style={{ marginTop: "1em" }}>
              <RoleFilter guildId={teamId} onChange={setCurrentFilter} />
            </Row>
          ) : null}

          {selectedFilterType === "timezone" ? (
            <Row style={{ marginTop: "1em" }}>
              <TimezoneFilter teamId={teamId} onChange={setCurrentFilter} />
            </Row>
          ) : null}

          {selectedFilterType === "date_joined" ? (
            <Row
              style={{
                marginTop: "1em",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <div>
                <span>Date joined range: </span>
                <RangePicker
                  defaultValue={[
                    moment(startTime, dateFormat),
                    moment(endTime, dateFormat),
                  ]}
                  style={{ borderRadius: "4px" }}
                  format={dateFormat}
                  disabledDate={disabledDate}
                  onChange={(date, dateString) => {
                    setCurrentFilter({
                      filter_type: "date_joined",
                      filter_settings: {
                        start_date: dateString[0],
                        end_date: dateString[1],
                      },
                    });
                    dateChanged(date, dateString);
                  }}
                />
              </div>
              {platform === "slack" ? (
                <p style={{ marginTop: "1em" }}>
                  We can only track join dates of members that have joined since
                  the date you joined Dots due to Slack API limitations.
                  <br />
                  {teamCreatedAt
                    ? `You joined Dots on ${teamCreatedAt}`
                    : "Please contact support to find your join date"}
                </p>
              ) : null}
            </Row>
          ) : null}

          {selectedFilterType === "channel_member" ? (
            <Row style={{ marginTop: "1em" }}>
              <ChannelMemberFilter
                teamId={teamId}
                onChange={setCurrentFilter}
              />
            </Row>
          ) : null}

          {selectedFilterType === "user_group" ? (
            <Row style={{ marginTop: "1em" }}>
              <GroupFilter teamId={teamId} onChange={setCurrentFilter} />
            </Row>
          ) : null}

          <Row style={{ marginTop: "1em" }}>
            <Button type="primary" onClick={createView}>
              Create view
            </Button>
          </Row>
        </Card>
      </Spin>
    </div>
  );
};

export default CreateNewView;
